/*
 * Vital Pantry - delivery date picker + made-to-order handling
 *
 * The delivery schedule is read LIVE from the Google Sheet on every page load.
 * The Sheet is the source of truth; there is no sync job and no API token.
 *
 * The metafield vitalpantry.delivery_zones is a fallback snapshot that renders
 * first, and it is being retired - it has been stale since it was seeded. This
 * file therefore treats it as optional: no snapshot is a valid state, and with
 * no schedule from any source the picker FAILS OPEN rather than blocking a
 * sale. See haveSchedule() and whenScheduleReady().
 *
 * Responsibilities:
 *   - collect a postcode on first visit, resolve it to a delivery zone
 *   - offer only the dates that zone actually delivers on, after cutoff /
 *     lead time / blackout / capacity filtering
 *   - block add-to-cart until a date exists
 *   - keep the choice on the cart as line-item-independent cart attributes
 *     so it reaches the order, and let the customer change it on the cart page,
 *     in the cart drawer, or from the floating bottom-centre button
 *   - flag products carrying the made-to-order tag, restrict them to the
 *     production window, and when a cart mixes made-to-order and ordinary
 *     items make the customer choose one delivery or two
 *
 * The cart carries readable dates only ("Wed 12 Aug 2026"). parseLabel() turns
 * them back into ISO internally.
 *
 * All date maths is done on pseudo-UTC midnights (Date.UTC) so that Melbourne
 * DST transitions cannot shift a delivery date by a day.
 */
(function () {
  'use strict';

  var cfg = window.VPDelivery;
  if (!cfg) return;

  /*
   * Minimum order value, in cents.
   *
   * Shopify has no minimum-order setting outside Plus, so the rule is enforced
   * here rather than in a script of its own - this file already owns the Check
   * Out button. Two scripts both toggling that button would fight each other:
   * whichever ran last would win, and a customer who then picked a delivery
   * date would have the button handed straight back under an under-minimum
   * cart.
   *
   * The amount lives in Settings > Custom data > Shop
   * (vitalpantry.min_order_value) so it can be changed, or switched off,
   * without a deploy - the same way the free delivery threshold is. 0 or
   * absent means no minimum, and the gate below then never fires.
   */
  var MIN_ORDER = Math.round((Number(cfg.minOrder) || 0) * 100);

  // The snapshot is on its way out, so nothing here may assume it exists.
  // An absent or empty metafield is a valid state: it just means we have no
  // schedule until the Sheet lands.
  // Tunables used to live inside the delivery_zones metafield, so retiring the
  // snapshot would have taken them with it. They are printed separately now.
  var SETTINGS = cfg.settings || {};

  // Posted orders (Australia-wide). Dormant unless the shop metafield
  // vitalpantry.post_nationwide is true. When on, a postcode outside every
  // delivery zone no longer lands on the waitlist: the customer can order
  // anything that can be posted, and products tagged melbourne-only (fridge,
  // frozen, fresh-baked) block checkout until they are removed.
  var POST_ON = SETTINGS.post_nationwide === true || SETTINGS.post_nationwide === 'true';
  var MELB_TAG = String(SETTINGS.melb_tag || 'melbourne-only').toLowerCase().trim();
  // Posted orders only: minimum spend in cents, and the postage wording shown
  // to the customer. The rate itself is charged by the Shopify shipping zone.
  var POST_MIN = Math.round((parseFloat(SETTINGS.post_min) || 0) * 100);
  var POST_RATE = String(SETTINGS.post_rate || '');
  // Regional Victoria is posted at its own rate. VIC postcodes are 3000-3999
  // and 8000-8999; every other postcode is interstate.
  var POST_RATE_VIC = String(SETTINGS.post_rate_vic || POST_RATE);
  function isVicPostcode(pc) { return /^[38][0-9]{3}$/.test(pc || ''); }
  function postRate() { return isVicPostcode(state.postcode) ? POST_RATE_VIC : POST_RATE; }

  function money(cents) {
    var d = cents / 100;
    return '$' + (d % 1 === 0 ? d.toFixed(0) : d.toFixed(2));
  }

  var DATA = cfg.data || {};
  DATA.zones = DATA.zones || {};
  DATA.postcodes = DATA.postcodes || {};
  DATA.timezone = DATA.timezone || SETTINGS.timezone;
  DATA.horizon_days = DATA.horizon_days || SETTINGS.horizon_days;
  DATA.capacity_buffer = DATA.capacity_buffer || SETTINGS.capacity_buffer;
  DATA.blackouts = DATA.blackouts || SETTINGS.blackouts || [];
  // One-off extra delivery dates ("2026-09-29") offered to EVERY zone on top of
  // its usual weekdays, e.g. to make up for a blacked-out long weekend. The
  // Sheet only knows weekdays, so a single date cannot live there.
  DATA.extra_dates = DATA.extra_dates || SETTINGS.extra_dates || [];
  // Dates closed for particular postcodes only ({"3806": ["2026-09-14"]}), for
  // when one area's run is off but the rest of its zone still goes out.
  DATA.postcode_blackouts = DATA.postcode_blackouts || SETTINGS.postcode_blackouts || {};

  // sheetCsv is now printed alongside the data rather than inside it, so that
  // deleting the snapshot cannot take the Sheet's own address with it.
  DATA.sheet_csv = cfg.sheetCsv || DATA.sheet_csv || '';
  if (!DATA.sheet_csv && !Object.keys(DATA.zones).length) return;

  // True once we hold a schedule from any source. Everything that would put a
  // question to the customer has to wait for this: asking someone for their
  // postcode when we have no zones to match it against can only ever tell a
  // real customer we do not deliver to them.
  function haveSchedule() { return Object.keys(DATA.zones).length > 0; }


  var TZ = DATA.timezone || 'Australia/Melbourne';
  var DAY = 86400000;
  var DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var MONTH = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  /**
   * Made-to-order config, from shop metafields via Liquid. Detection is by
   * product TAG so any supplier's products can be marked, and the wording is
   * editable in admin without a deploy.
   *
   * The production window is derived from the cutoff day rather than being
   * configured separately: a run closes on the cutoff day and covers the next
   * Saturday through the Wednesday after it. Two settings that must agree are
   * two settings that will eventually disagree.
   */
  var MTO = cfg.mto && cfg.mto.tag ? cfg.mto : null;
  if (MTO && !MTO.window) {
    var cutDow = (parseCutoffDow(MTO.cutoff) === null) ? 1 : parseCutoffDow(MTO.cutoff);
    var toSaturday = ((6 - cutDow + 7) % 7) || 7;
    MTO.window = { from_days: toSaturday, to_days: toSaturday + 4 };
  }

  // Standalone so it can run before parseCutoff is defined below.
  function parseCutoffDow(str) {
    var m = String(str || '').trim().match(/^([A-Za-z]{3,})\s+/);
    if (!m) return null;
    var want = m[1].slice(0, 3).toLowerCase();
    var names = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
    var at = names.indexOf(want);
    return at === -1 ? null : at;
  }

  var ATTR_POSTCODE = 'Delivery Postcode';
  var ATTR_ZONE = 'Delivery Zone';
  var ATTR_DATE = 'Delivery Date';
  var ATTR_SPLIT = 'Bakery Delivery';
  var ATTR_BAKERY_DATE = 'Bakery Delivery Date';

  var LS_KEY = 'vp_delivery_v1';
  var LS_DISMISS = 'vp_delivery_gate_dismissed_v1';

  // Last schedule successfully read from the Sheet, kept so a customer who has
  // been here before is never at the mercy of Google being reachable. The _v1
  // suffix is the cache's own format: bump it and every stored copy is ignored
  // rather than misread.
  var LS_SCHEDULE = 'vp_delivery_schedule_v1';
  var SCHEDULE_MAX_AGE_MS = 30 * 86400000;

  // Populated below: the cart stores readable labels, which have to be parsed
  // back before any date maths can run.
  var state = {
    postcode: (cfg.initial && cfg.initial.postcode) || '',
    zoneKey: '',
    dateISO: '',
    split: (cfg.initial && cfg.initial.split) || '',      // '' | 'Together' | 'Separate'
    bakeryISO: '',
    posted: false         // postcode is outside every zone and the order will be posted
  };

  // Cart composition, refreshed from /cart.js so it survives AJAX add-to-cart.
  var cart = { hasBakery: false, hasOther: false, needsDelivery: true, melbOnly: [], total: null };

  // Seeded from Liquid so the minimum bites on first paint instead of only
  // once /cart.js answers. Without it the Check Out button is live for a
  // moment on an under-minimum cart, which is long enough to click.
  if (typeof cfg.cartTotal === 'number') cart.total = cfg.cartTotal;
  var suppressCartWatch = false;

  // False until /cart.js has answered. Was called `cartKnown` and guarded two
  // different things: validating against the stale snapshot, and validating
  // against a cart we had not read yet. The snapshot is gone, but the cart
  // half remains and is not optional - needsBakeryDates() reads
  // cart.hasBakery, so before the cart lands a perfectly good bakery date
  // looks invalid and gets thrown away. Renamed for what it actually means.
  var cartKnown = false;

  var pendingForm = null;
  var root = null;

  /* ---------------------------------------------------------------- time */

  // Current Melbourne wall clock, expressed as pseudo-UTC milliseconds.
  function melbNow() {
    var f = new Intl.DateTimeFormat('en-CA', {
      timeZone: TZ,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false
    });
    var p = {};
    f.formatToParts(new Date()).forEach(function (x) { p[x.type] = x.value; });
    var hh = parseInt(p.hour, 10);
    if (hh === 24) hh = 0; // some engines report midnight as 24
    return {
      dayMs: Date.UTC(+p.year, +p.month - 1, +p.day),
      nowMs: Date.UTC(+p.year, +p.month - 1, +p.day, hh, parseInt(p.minute, 10))
    };
  }

  function toISO(ms) {
    var d = new Date(ms);
    var m = d.getUTCMonth() + 1;
    var day = d.getUTCDate();
    return d.getUTCFullYear() + '-' + (m < 10 ? '0' + m : m) + '-' + (day < 10 ? '0' + day : day);
  }

  function fromISO(iso) {
    var b = String(iso || '').split('-');
    if (b.length !== 3) return NaN;
    return Date.UTC(+b[0], +b[1] - 1, +b[2]);
  }

  // "Wed 12 Aug 2026". The year matters: this string is the only record of the
  // date on the order, so it has to stand on its own months later.
  function longLabel(ms) {
    var d = new Date(ms);
    return DOW[d.getUTCDay()] + ' ' + d.getUTCDate() + ' ' +
           MONTH[d.getUTCMonth()] + ' ' + d.getUTCFullYear();
  }

  function labelFor(iso) { return iso ? longLabel(fromISO(iso)) : ''; }

  /**
   * Inverse of longLabel. The cart no longer carries a separate ISO attribute,
   * so the readable label is parsed back when state is restored from the cart.
   * Anything unrecognised returns '' rather than a wrong date.
   */
  function parseLabel(label) {
    var m = String(label || '').trim()
      .match(/^[A-Za-z]{3}\s+(\d{1,2})\s+([A-Za-z]{3})\s+(\d{4})$/);
    if (!m) return '';
    var month = MONTH.indexOf(m[2].charAt(0).toUpperCase() + m[2].slice(1).toLowerCase());
    if (month === -1) return '';
    var day = parseInt(m[1], 10);
    var ms = Date.UTC(parseInt(m[3], 10), month, day);
    // Reject 31 Feb and friends: Date.UTC silently rolls them over.
    return new Date(ms).getUTCDate() === day ? toISO(ms) : '';
  }

  // "Mon 18:00" -> {dow:1,h:18,min:0}   "18:00" -> {dow:null,h:18,min:0}
  function parseCutoff(str) {
    var s = String(str || '').trim();
    if (!s) return null;
    var dow = null;
    var m = s.match(/^([A-Za-z]{3,})\s+(.*)$/);
    if (m) {
      var want = m[1].slice(0, 3).toLowerCase();
      for (var i = 0; i < DOW.length; i++) {
        if (DOW[i].toLowerCase() === want) { dow = i; s = m[2].trim(); break; }
      }
    }
    var t = s.match(/^(\d{1,2}):(\d{2})$/);
    if (!t) return null;
    return { dow: dow, h: +t[1], min: +t[2] };
  }

  // The instant orders close for a delivery on dateMs.
  function cutoffMs(dateMs, zone) {
    var c = parseCutoff(zone.cutoff);
    if (!c) return -Infinity; // no cutoff configured -> never closed
    var day;
    if (c.dow === null) {
      day = dateMs - (zone.lead_days || 0) * DAY;
    } else {
      day = dateMs - 7 * DAY;
      for (var k = 1; k <= 7; k++) {
        var t = dateMs - k * DAY;
        if (new Date(t).getUTCDay() === c.dow) { day = t; break; }
      }
    }
    return day + c.h * 3600000 + c.min * 60000;
  }

  /* ------------------------------------------------------- made to order */

  /**
   * Made-to-order items are produced in weekly runs. Ordering closes on the
   * cutoff day and that run covers the Saturday to Wednesday that follow.
   * Returns the instant ordering closes for a delivery on dateMs, or null if
   * that weekday is never a delivery day for the run (Thu and Fri).
   */
  function bakeryCutoffMs(dateMs) {
    if (!MTO) return null;
    var c = parseCutoff(MTO.cutoff) || { dow: 1, h: 23, min: 59 };
    var from = (MTO.window && MTO.window.from_days) || 5;
    var to = (MTO.window && MTO.window.to_days) || 9;

    for (var off = from; off <= to; off++) {
      var cand = dateMs - off * DAY;
      if (new Date(cand).getUTCDay() === c.dow) {
        return cand + c.h * 3600000 + c.min * 60000;
      }
    }
    return null; // outside the production window entirely
  }

  function isBakeryDate(dateMs, nowMs) {
    var co = bakeryCutoffMs(dateMs);
    return co !== null && co > nowMs;
  }

  /**
   * Is this cart line a made-to-order product?
   *
   * /cart.js does not expose tags, but /products/<handle>.js does, so each
   * unique handle is looked up once and remembered. That keeps the rule keyed
   * on the tag alone: no handle list to publish, and tagging a new product
   * takes effect immediately with nothing to re-sync.
   */
  var TAG_CACHE_KEY = 'vp_mto_tags_v1';
  var tagCache = {};

  try { tagCache = JSON.parse(localStorage.getItem(TAG_CACHE_KEY) || '{}'); } catch (e) { tagCache = {}; }

  function saveTagCache() {
    try { localStorage.setItem(TAG_CACHE_KEY, JSON.stringify(tagCache)); } catch (e) { /* noop */ }
  }

  function isMadeToOrder(handle) {
    if (!MTO || !handle) return Promise.resolve(false);
    if (Object.prototype.hasOwnProperty.call(tagCache, handle)) {
      return Promise.resolve(tagCache[handle]);
    }
    return fetch('/products/' + encodeURIComponent(handle) + '.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (p) {
        var want = String(MTO.tag).toLowerCase();
        var hit = (p.tags || []).some(function (t) {
          return String(t).toLowerCase().trim() === want;
        });
        tagCache[handle] = hit;
        saveTagCache();
        return hit;
      })
      .catch(function () {
        // Unknown rather than cached: a network blip must not permanently
        // brand a bakery item as an ordinary grocery.
        return false;
      });
  }

  var MELB_CACHE_KEY = 'vp_melb_tags_v1';
  var MELB_MAX_AGE_MS = 12 * 3600000;
  var melbCache = {};
  try { melbCache = JSON.parse(localStorage.getItem(MELB_CACHE_KEY) || '{}'); } catch (e) { melbCache = {}; }

  function isMelbOnly(handle) {
    if (!POST_ON || !handle) return Promise.resolve(false);
    var hit = melbCache[handle];
    if (hit && new Date().getTime() - hit.at < MELB_MAX_AGE_MS) return Promise.resolve(hit.v);
    return fetch('/products/' + encodeURIComponent(handle) + '.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (p) {
        var v = (p.tags || []).some(function (t) {
          return String(t).toLowerCase().trim() === MELB_TAG;
        });
        melbCache[handle] = { v: v, at: new Date().getTime() };
        try { localStorage.setItem(MELB_CACHE_KEY, JSON.stringify(melbCache)); } catch (e) { /* noop */ }
        return v;
      })
      .catch(function () { return false; });
  }

  // Cents still needed to reach the posted-order minimum (0 when met).
  function shortfall() {
    return POST_MIN > 0 && cart.total !== null && cart.total < POST_MIN ? POST_MIN - cart.total : 0;
  }

  // True when the customer's postcode is outside every zone and posting is on.
  function isPosted() {
    return POST_ON && state.posted && /^\d{4}$/.test(state.postcode || '') && !zoneFor(state.postcode);
  }

  /* ----------------------------------------------------- live sheet load
   *
   * The Google Sheet is read straight from the browser. Google serves the CSV
   * export with permissive CORS, so no server, token or sync job is involved -
   * an edit in the Sheet reaches customers on their next page load.
   *
   * The metafield snapshot baked into the page by Liquid is still the thing
   * that renders first, and stays in use if the Sheet is unreachable, so a
   * Google outage or a broken share setting cannot take the picker down.
   *
   * The Sheet must stay link-viewable. If sharing is set back to private this
   * silently falls back to the snapshot, which is the safe direction to fail.
   */

  var DAY_NAMES = {
    mon: 'Mon', monday: 'Mon',
    tue: 'Tue', tues: 'Tue', tuesday: 'Tue',
    wed: 'Wed', weds: 'Wed', wednesday: 'Wed',
    thu: 'Thu', thur: 'Thu', thurs: 'Thu', thursday: 'Thu',
    fri: 'Fri', friday: 'Fri',
    sat: 'Sat', saturday: 'Sat',
    sun: 'Sun', sunday: 'Sun'
  };
  var DAY_ORDER = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  var DAY_LONG = {
    Mon: 'Monday', Tue: 'Tuesday', Wed: 'Wednesday', Thu: 'Thursday',
    Fri: 'Friday', Sat: 'Saturday', Sun: 'Sunday'
  };

  // Minimal RFC-4180 reader: handles quoted fields, embedded commas and the
  // CRLF line endings Google's export uses. split(',') would corrupt any
  // suburb containing a comma.
  function parseCsv(text) {
    var rows = [];
    var row = [];
    var field = '';
    var quoted = false;

    for (var i = 0; i < text.length; i++) {
      var c = text.charAt(i);

      if (quoted) {
        if (c === '"') {
          if (text.charAt(i + 1) === '"') { field += '"'; i++; }
          else quoted = false;
        } else field += c;
        continue;
      }

      if (c === '"') { quoted = true; }
      else if (c === ',') { row.push(field); field = ''; }
      else if (c === '\n') { row.push(field); rows.push(row); row = []; field = ''; }
      else if (c !== '\r') { field += c; }
    }
    if (field !== '' || row.length) { row.push(field); rows.push(row); }
    return rows;
  }

  function parseDays(raw) {
    var out = [];
    String(raw || '').split(/[&,/+]| and /i).forEach(function (tok) {
      var d = DAY_NAMES[tok.trim().toLowerCase()];
      if (d && out.indexOf(d) === -1) out.push(d);
    });
    out.sort(function (a, b) { return DAY_ORDER.indexOf(a) - DAY_ORDER.indexOf(b); });
    return out;
  }

  /**
   * Turn the Sheet's rows into the same {zones, postcodes} shape the metafield
   * holds. A "run" is the set of days a postcode is served on, so postcodes
   * sharing a day pattern share a run.
   *
   * Returns null if the result looks broken, so a mangled Sheet leaves the
   * snapshot in place rather than emptying the store's delivery areas.
   */
  function buildFromRows(rows) {
    if (!rows || rows.length < 2) return null;

    var norm = rows[0].map(function (h) {
      return String(h || '').toLowerCase().replace(/[^a-z]/g, '');
    });
    var col = function (names) {
      for (var i = 0; i < names.length; i++) {
        var at = norm.indexOf(names[i].toLowerCase().replace(/[^a-z]/g, ''));
        if (at > -1) return at;
      }
      return -1;
    };

    var iSub = col(['Suburb']);
    var iPc = col(['Postcode', 'Post code']);
    var iDay = col(['Delivery day', 'Delivery days']);
    var iCut = col(['Cutoff', 'Cut off']);
    var iLead = col(['Lead days']);
    var iCap = col(['Daily cap', 'Cap']);
    var iAct = col(['Active']);

    if (iPc === -1 || iDay === -1) return null;

    var cell = function (r, i) { return i === -1 ? '' : String(r[i] == null ? '' : r[i]).trim(); };
    var zones = {};
    var postcodes = {};

    for (var r = 1; r < rows.length; r++) {
      var row = rows[r];
      if (!row || !row.length) continue;

      var active = cell(row, iAct).toLowerCase();
      if (active === 'false' || active === 'no' || active === '0') continue;

      var pc = cell(row, iPc);
      if (!/^\d{4}$/.test(pc)) continue;

      var days = parseDays(cell(row, iDay));
      if (!days.length) continue;

      var key = days.join('-').toLowerCase();
      if (!zones[key]) {
        zones[key] = {
          name: days.length === 1
            ? DAY_LONG[days[0]] + ' deliveries'
            : days.join(' & ') + ' deliveries',
          days: days,
          cutoff: cell(row, iCut) || '18:00',
          lead_days: parseInt(cell(row, iLead) || '1', 10) || 0,
          daily_cap: parseInt(cell(row, iCap) || '0', 10) || 0,
          min_order: 0,
          suburbs: []
        };
      }
      var suburb = cell(row, iSub);
      if (suburb && zones[key].suburbs.indexOf(suburb) === -1) zones[key].suburbs.push(suburb);

      if (!postcodes[pc]) postcodes[pc] = key;   // first row wins, as in the snapshot
    }

    // A Sheet that yields almost nothing is far more likely to be broken than
    // to be a real decision to stop delivering everywhere.
    if (Object.keys(postcodes).length < 5) return null;

    Object.keys(zones).forEach(function (k) { zones[k].suburbs.sort(); });
    return { zones: zones, postcodes: postcodes };
  }

  function loadSheet() {
    var url = DATA.sheet_csv;
    if (!url) return Promise.resolve(false);

    return fetch(url, { cache: 'no-store' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (text) {
        var built = buildFromRows(parseCsv(text));
        if (!built) throw new Error('sheet did not parse into a usable schedule');
        DATA.zones = built.zones;
        DATA.postcodes = built.postcodes;
        saveSchedule(built);
        return true;
      })
      .catch(function (e) {
        // Snapshot stays in force. Not a customer-facing failure.
        console.warn('[vp-delivery] using the published snapshot:', e.message);
        return false;
      });
  }

  /* --------------------------------------------------------------- zones */

  function zoneFor(postcode) {
    var key = DATA.postcodes && DATA.postcodes[String(postcode).trim()];
    return key ? { key: key, zone: DATA.zones[key] } : null;
  }

  function usedCount(zoneKey, iso) {
    var u = DATA.used && DATA.used[zoneKey];
    return (u && u[iso]) || 0;
  }

  function isFull(zoneKey, zone, iso) {
    var cap = zone.daily_cap || 0;
    if (cap <= 0) return false;
    var buffer = DATA.capacity_buffer || 0;
    return usedCount(zoneKey, iso) >= (cap - buffer);
  }

  /**
   * Every date this zone can still be booked for, soonest first.
   * bakeryOnly restricts the list to dates the production run can also serve.
   */
  function availableDates(zoneKey, bakeryOnly) {
    var zone = DATA.zones[zoneKey];
    if (!zone) return [];
    var t = melbNow();
    var horizon = DATA.horizon_days || 28;
    // Every caller asks about the customer's own zone, so their postcode's
    // closures apply on top of the store-wide ones.
    var blackouts = (DATA.blackouts || [])
      .concat(DATA.postcode_blackouts[String(state.postcode || '').trim()] || []);
    var extras = DATA.extra_dates || [];
    var lead = zone.lead_days || 0;
    var days = zone.days || [];
    var out = [];

    for (var i = 0; i <= horizon; i++) {
      var ms = t.dayMs + i * DAY;
      var iso = toISO(ms);
      // An extra date opens any weekday; it still has to clear lead time,
      // cutoff, capacity and the bakery window like any other. Blackouts win.
      if (days.indexOf(DOW[new Date(ms).getUTCDay()]) === -1 && extras.indexOf(iso) === -1) continue;
      if (ms < t.dayMs + lead * DAY) continue;
      if (blackouts.indexOf(iso) > -1) continue;
      if (cutoffMs(ms, zone) <= t.nowMs) continue;
      if (isFull(zoneKey, zone, iso)) continue;
      if (bakeryOnly && !isBakeryDate(ms, t.nowMs)) continue;
      out.push({ ms: ms, iso: iso, label: longLabel(ms) });
    }
    return out;
  }

  // True when the whole order has to sit on a made-to-order eligible date.
  function needsBakeryDates() {
    if (!cart.hasBakery) return false;
    if (!cart.hasOther) return true;          // made to order only
    return state.split === 'Together';        // mixed, customer chose combined
  }

  function isMixed() { return cart.hasBakery && cart.hasOther; }

  function nextBakeryDate() {
    var list = availableDates(state.zoneKey, true);
    return list.length ? list[0] : null;
  }

  function isStillValid() {
    if (!state.zoneKey || !state.dateISO) return false;
    return availableDates(state.zoneKey, needsBakeryDates())
      .some(function (d) { return d.iso === state.dateISO; });
  }

  // "$39" / "$8.50" - whole dollars stay whole, which reads better in a nudge.
  function minMoney(cents) {
    var d = cents / 100;
    return '$' + (d % 1 === 0 ? d.toFixed(0) : d.toFixed(2));
  }

  // Fails OPEN while the cart total is unknown, like the rest of this file: a
  // greyed Check Out over a cart we have not read yet would block real sales.
  // In practice it is known on first paint, because the snippet seeds it from
  // Liquid before this script runs.
  function meetsMinimum() {
    if (!(MIN_ORDER > 0)) return true;
    if (cart.total === null) return true;
    return cart.total >= MIN_ORDER;
  }

  // A mixed cart is only fully answered once the split question has one.
  function isComplete() {
    // Nothing in the cart ships, so there is no date to choose and the gate
    // must stand down. Without this a gift-card-only cart cannot check out at
    // all, and an interstate buyer - exactly who buys a gift card for a
    // Melbourne friend - dead-ends on the out-of-area waitlist screen.
    if (cartKnown && !cart.needsDelivery) return true;
    if (isPosted()) return !cart.melbOnly.length && shortfall() === 0;
    if (!state.zoneKey || !state.dateISO) return false;
    if (isMixed() && !state.split) return false;
    if (isMixed() && state.split === 'Separate' && !state.bakeryISO) return false;
    return true;
  }

  /* ------------------------------------------------------------ persistence */

  function loadLocal() {
    // The cart wins over localStorage: it is the copy that reaches the order.
    if (cfg.initial) {
      state.dateISO = state.dateISO || parseLabel(cfg.initial.dateLabel) || '';
      state.bakeryISO = state.bakeryISO || parseLabel(cfg.initial.bakeryLabel) || '';
    }
    try {
      var raw = JSON.parse(localStorage.getItem(LS_KEY) || '{}');
      state.postcode = state.postcode || raw.postcode || '';
      state.zoneKey = state.zoneKey || raw.zoneKey || '';
      state.dateISO = state.dateISO || raw.dateISO || '';
      state.split = state.split || raw.split || '';
      state.bakeryISO = state.bakeryISO || raw.bakeryISO || '';
      state.posted = state.posted || !!raw.posted;
    } catch (e) { /* private mode */ }
  }

  function saveLocal() {
    // Before the cart is known, an empty dateISO means "not loaded yet", not "cleared".
    // Persisting it would destroy the stored answer for good.
    if (!cartKnown && !state.dateISO && !state.posted) return;
    try { localStorage.setItem(LS_KEY, JSON.stringify(state)); } catch (e) { /* private mode */ }
  }

  /**
   * The cached schedule holds only what the picker actually reads back:
   * zone days/cutoff/lead/cap and the postcode map. zone.suburbs is built for
   * parity with the metafield shape but never read at runtime, so it is
   * dropped here and takes about 8KB of dead weight with it.
   */
  function saveSchedule(built) {
    try {
      var zones = {};
      Object.keys(built.zones).forEach(function (k) {
        var z = built.zones[k];
        zones[k] = {
          name: z.name, days: z.days, cutoff: z.cutoff,
          lead_days: z.lead_days, daily_cap: z.daily_cap
        };
      });
      localStorage.setItem(LS_SCHEDULE, JSON.stringify({
        v: 1, at: new Date().getTime(), zones: zones, postcodes: built.postcodes
      }));
    } catch (e) { /* private mode, or quota - the Sheet still worked */ }
  }

  /**
   * Returns null unless the cache is present, the right format, recent, and
   * big enough to be a real schedule rather than a truncated one. Same
   * five-postcode floor buildFromRows() applies to the Sheet itself: a copy
   * that fails it would refuse real customers.
   */
  function loadSchedule() {
    try {
      var raw = JSON.parse(localStorage.getItem(LS_SCHEDULE) || 'null');
      if (!raw || raw.v !== 1 || !raw.zones || !raw.postcodes) return null;
      if (!raw.at || new Date().getTime() - raw.at > SCHEDULE_MAX_AGE_MS) return null;
      if (Object.keys(raw.postcodes).length < 5) return null;
      if (!Object.keys(raw.zones).length) return null;
      return raw;
    } catch (e) {
      return null;
    }
  }

  function cartUpdateUrl() {
    return (window.routes && window.routes.cart_update_url) || '/cart/update.js';
  }

  // Refresh cart composition from the source of truth rather than trusting a
  // Liquid snapshot, which goes stale the moment an AJAX add-to-cart happens.
  function refreshCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) {
        var items = c.items || [];
        cart.total = c.total_price || 0;
        var handles = [];
        items.forEach(function (it) {
          if (it.handle && handles.indexOf(it.handle) === -1) handles.push(it.handle);
        });

        var melbWork = Promise.all(handles.map(isMelbOnly)).then(function (mflags) {
          var names = [];
          items.forEach(function (it) {
            if (!mflags[handles.indexOf(it.handle)]) return;
            var n = it.product_title || it.title;
            if (names.indexOf(n) === -1) names.push(n);
          });
          cart.melbOnly = names;
        });

        return melbWork.then(function () { return Promise.all(handles.map(isMadeToOrder)); }).then(function (flags) {
          var map = {};
          handles.forEach(function (h, i) { map[h] = flags[i]; });

          // Gift cards are emailed, not delivered. A cart holding nothing that
          // requires shipping has no delivery date to ask for.
          cart.needsDelivery = items.length === 0 || items.some(function (it) { return it.requires_shipping !== false; });

          // Needed by meetsMinimum(). Read from /cart.js rather than a Liquid
          // snapshot for the same reason as everything else here: the snapshot
          // is wrong the moment an AJAX add-to-cart happens.
          cart.total = c.total_price || 0;

          cart.hasBakery = items.some(function (it) { return map[it.handle]; }); cart.hasOther = items.some(function (it) { return !map[it.handle]; }); // Previously this cleared state.split/bakeryISO whenever the cart had // no bakery items at that moment, which forced the split question to // be re-asked the next time a bakery item was added back. Once the // customer has answered it for this session, keep the answer.
        });
      })
      .catch(function () { /* leave the last known composition in place */ });
  }

  /**
   * The theme's product-form.js does not publish PUB_SUB_EVENTS.cartUpdate on
   * add-to-cart - it calls cartNotification.renderContents() directly - so
   * subscribing to that event never fires here. Watching cart writes at the
   * fetch layer is the only way to notice that a cart just became mixed, which
   * is exactly when the customer has to be asked how to split the delivery.
   */
  function watchCartMutations() {
    var orig = window.fetch;
    if (!orig || orig.__vpdWrapped) return;

    var wrapped = function (input) {
      var url = '';
      try { url = typeof input === 'string' ? input : (input && input.url) || ''; } catch (e) { /* opaque */ }
      var p = orig.apply(this, arguments);

      if (/\/cart\/(add|change|update|clear)/.test(url)) {
        p.then(function () {
          // Captured at resolve time: our own write clears the flag afterwards,
          // and reading it inside the timeout would re-enter.
          var mine = suppressCartWatch;
          setTimeout(function () { if (!mine) onCartMutated(); }, 0);
        }, function () { /* the theme reports its own failures */ });
      }
      return p;
    };

    wrapped.__vpdWrapped = true;
    window.fetch = wrapped;
  }

  function onCartMutated() {
    return refreshCart().then(function () {
      reconcile();
      injectCartEditors();
      renderSummary();
      if (root && !root.hidden) return;   // customer is already mid-decision
      if (!haveSchedule()) return;        // fail open: nothing to ask against
      if (isComplete()) return;
      openModal(firstUnansweredStep());
    });
  }

  function pushToCart() {
    var zone = DATA.zones[state.zoneKey];
    var attrs = {};
    attrs[ATTR_POSTCODE] = state.postcode || '';
    attrs[ATTR_ZONE] = zone ? zone.name : '';
    attrs[ATTR_DATE] = labelFor(state.dateISO);
    attrs[ATTR_SPLIT] = cart.hasBakery ? (state.split || (cart.hasOther ? '' : 'Together')) : '';
    attrs[ATTR_BAKERY_DATE] = labelFor(bakeryDateISO());
    if (isPosted()) {
      attrs[ATTR_ZONE] = 'Posted (Australia-wide)';
      attrs[ATTR_DATE] = '';
      attrs[ATTR_SPLIT] = '';
      attrs[ATTR_BAKERY_DATE] = '';
    }

    suppressCartWatch = true;   // our own write must not re-trigger the watcher
    return fetch(cartUpdateUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    })
      .catch(function (e) { console.error('[vp-delivery] cart update failed', e); })
      .then(function (r) { suppressCartWatch = false; return r; });
  }

  // When everything travels together the bakery date is simply the main date.
  function bakeryDateISO() {
    if (!cart.hasBakery) return '';
    if (state.split === 'Separate') return state.bakeryISO || '';
    return state.dateISO || '';
  }

  /* ------------------------------------------------------------------ ui */

  function q(sel) { return root.querySelector(sel); }

  function openModal(step) {
    root.hidden = false;
    document.body.classList.add('vpd-open');
    showStep(step || firstUnansweredStep());
    var focusable = root.querySelector('input:not([type=hidden]), button');
    if (focusable) focusable.focus();
  }

  // Which step the modal is showing right now, for redrawing in place.
  function currentStep() {
    var names = ['postcode', 'split', 'date', 'waitlist', 'posted'];
    for (var i = 0; i < names.length; i++) {
      var el = q('[data-vpd-step="' + names[i] + '"]');
      if (el && !el.hidden) return names[i];
    }
    return firstUnansweredStep();
  }

  function firstUnansweredStep() {
    if (!state.postcode) return 'postcode';
    if (isPosted()) return 'posted';
    if (isMixed() && !state.split) return 'split';
    return 'date';
  }

  function closeModal() {
    root.hidden = true;
    document.body.classList.remove('vpd-open');
    pendingForm = null;
  }

  function showStep(name) {
    ['postcode', 'split', 'date', 'waitlist', 'posted'].forEach(function (s) {
      var el = q('[data-vpd-step="' + s + '"]');
      if (el) el.hidden = (s !== name);
    });
    if (name === 'posted') renderPosted();
    if (name === 'date') renderDates();
    if (name === 'split') renderSplit();
  }

  /**
   * The dates quoted on each option have to be the dates the customer will
   * actually get, otherwise the following step contradicts the choice they just
   * made. A date they already picked wins over "the earliest available".
   */
  function renderSplit() {
    var normal = availableDates(state.zoneKey, false);
    var bakeryList = availableDates(state.zoneKey, true);

    var keeps = function (list) {
      return state.dateISO && list.some(function (d) { return d.iso === state.dateISO; })
        ? { iso: state.dateISO, label: labelFor(state.dateISO) }
        : (list.length ? list[0] : null);
    };

    var restDate = keeps(normal);
    var bakeryDate = keeps(bakeryList);

    var elTogether = q('[data-vpd-split-together-date]');
    var elRest = q('[data-vpd-split-rest-date]');
    var elBakery = q('[data-vpd-split-bakery-date]');

    if (elTogether) elTogether.textContent = bakeryDate ? bakeryDate.label : 'no date available';
    if (elRest) elRest.textContent = restDate ? restDate.label : 'no date available';
    if (elBakery) elBakery.textContent = bakeryDate ? bakeryDate.label : 'the next bakery round';

    // With no bakery date open, splitting is the only workable answer.
    var togetherBtn = q('[data-vpd-split="Together"]');
    if (togetherBtn) togetherBtn.disabled = !bakeryDate;
  }

  function renderDates() {
    var zone = DATA.zones[state.zoneKey];
    var bakeryOnly = needsBakeryDates();
    var list = q('[data-vpd-dates]');
    var head = q('[data-vpd-zone-name]');
    var note = q('[data-vpd-date-note]');

    if (head) head.textContent = zone ? zone.name : '';

    // Only a mixed cart has a split choice to go back to.
    var changeSplit = q('[data-vpd-change-split]');
    if (changeSplit) changeSplit.hidden = !isMixed();

    if (note) {
      if (bakeryOnly && MTO && MTO.note) {
        note.textContent = MTO.note;
        note.hidden = false;
      } else {
        note.hidden = true;
      }
    }
    if (!list) return;

    var dates = availableDates(state.zoneKey, bakeryOnly);
    list.innerHTML = '';

    if (!dates.length) {
      var p = document.createElement('p');
      p.className = 'vpd-empty';
      p.textContent = bakeryOnly
        ? 'No made-to-order delivery dates are open for ' + (zone ? zone.name : 'your area') +
          ' right now. Please contact us and we will sort a slot out for you.'
        : 'No delivery dates are open for ' + (zone ? zone.name : 'your area') +
          ' right now. Please contact us and we will sort a slot out for you.';
      list.appendChild(p);
      return;
    }

    dates.forEach(function (d) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'vpd-date' + (d.iso === state.dateISO ? ' is-selected' : '');
      b.setAttribute('data-vpd-pick', d.iso);
      b.innerHTML = '<span class="vpd-date__day">' + d.label + '</span>';
      list.appendChild(b);
    });
  }

  function renderSummary() {
    var zone = DATA.zones[state.zoneKey];
    var label = labelFor(state.dateISO);
    var posted = isPosted();
    var text = posted
      ? 'Posted Australia-wide · ' + state.postcode
      : label
      ? 'Selected delivery date: ' + label + (state.postcode ? ' · ' + state.postcode : '')
      : 'Choose a delivery date';

    document.querySelectorAll('[data-vpd-summary]').forEach(function (el) {
      el.textContent = text;
    });
    document.querySelectorAll('[data-vpd-zone-summary]').forEach(function (el) {
      el.textContent = zone ? zone.name : '';
    });
    document.querySelectorAll('[data-vpd-eta]').forEach(function (el) {
      if (posted) {
        el.textContent = (cfg.product && cfg.product.melbOnly)
          ? 'Melbourne delivery only. This item can\'t be posted to ' + state.postcode + '.'
          : 'Posted to ' + state.postcode;
        el.hidden = false;
      } else if (label) {
        el.textContent = 'Delivered ' + label;
        el.hidden = false;
      } else {
        el.hidden = true;
      }
    });

    // Second delivery line, only when the order is actually being split.
    var showBakery = cart.hasBakery && state.split === 'Separate' && !!state.bakeryISO;
    document.querySelectorAll('[data-vpd-bakery-line]').forEach(function (el) {
      el.hidden = !showBakery;
      if (showBakery) el.textContent = 'Bakery items: ' + labelFor(state.bakeryISO);
    });

    var pill = document.querySelector('[data-vpd-pill]');
    if (pill) {
      pill.classList.toggle('vpd-pill--unset', !label && !posted);
      pill.setAttribute('aria-label', label
        ? 'Selected delivery date ' + label + '. Change delivery date'
        : 'Choose a delivery date');
    }
    // The "Change" affordance only makes sense once there is something to change.
    var cta = document.querySelector('[data-vpd-pill-cta]');
    if (cta) cta.hidden = !label && !posted;

    syncCheckoutGate();
  }

  // Cart page and drawer: checkout stays disabled until the choice is complete.
  function syncCheckoutGate() {
    // With no schedule we are failing open, so the button must not be dimmed
    // and no warning shown: a greyed Check Out that actually works is worse
    // than no signal at all.
    // The minimum deliberately does NOT fail open alongside the schedule. A
    // missing Google Sheet is a reason to let someone through; a cart under
    // the minimum is not.
    var minOk = meetsMinimum();
    var ok = (!haveSchedule() || isComplete()) && minOk;

    // A cart with nothing to deliver should not be carrying a delivery date
    // editor either - "Choose a delivery date" above a single gift card reads
    // like something has gone wrong.
    var showEditor = !cartKnown || cart.needsDelivery;
    document.querySelectorAll('[data-vpd-cart]').forEach(function (box) {
      box.hidden = !showEditor;
    });

    document.querySelectorAll('[name="checkout"]').forEach(function (btn) {
      if (btn.hasAttribute('data-vpd-ignore')) return;
      btn.classList.toggle('vpd-blocked', !ok);
    });

    // The minimum takes the message whenever it is the blocker: it is the only
    // one of these the customer cannot resolve inside the modal.
    var message;
    var posted = isPosted() ? postedProblems() : [];
    if (posted.length) {
      // A posted order's own minimum is always the higher one, so it speaks
      // for both.
      message = posted.join(' ');
    } else if (!minOk) {
      message = 'Minimum order ' + minMoney(MIN_ORDER) + '. Please add ' +
        minMoney(MIN_ORDER - (cart.total || 0)) + ' more to check out.';
    } else if (isMixed() && state.dateISO && !state.split) {
      message = 'Your order has made-to-order items. Please choose how you would like it delivered.';
    } else {
      message = 'Please choose a delivery date before checking out.';
    }

    /*
     * The cart PAGE carries Apple Pay / Shop Pay express buttons
     * (shopify-accelerated-checkout-cart inside #dynamic-checkout-cart). They
     * are not [name="checkout"] and they do not submit the cart form, so
     * neither the click guard nor the submit guard can see them - a tap there
     * goes straight to a payment sheet, past the delivery date as well as the
     * minimum. Hiding them while the cart is not ready is the only reliable
     * gate; they come straight back the moment it is. The cart DRAWER has no
     * express buttons, so there is nothing to do there.
     */
    document.querySelectorAll('#dynamic-checkout-cart, .cart__dynamic-checkout-buttons').forEach(function (el) {
      el.style.display = ok ? '' : 'none';
    });
    document.querySelectorAll('[data-vpd-cart-warning]').forEach(function (warn) {
      warn.hidden = ok;
      warn.textContent = message;
    });
  }

  // Everything stopping a posted order from checking out, as sentences.
  function postedProblems() {
    var out = [];
    if (cart.melbOnly.length) {
      out.push('These items are Melbourne delivery only and can\'t be posted to ' + state.postcode + ': ' +
        cart.melbOnly.join(', ') + '. Please remove them to check out.');
    }
    var short = shortfall();
    if (short > 0) {
      out.push('Posted orders have a ' + money(POST_MIN) + ' minimum. Add ' + money(short) + ' more to check out.');
    }
    return out;
  }

  /* ------------------------------------------------------------- actions */

  function submitPostcode() {
    var input = q('[data-vpd-postcode-input]');
    var err = q('[data-vpd-postcode-error]');
    var raw = (input.value || '').trim();

    if (!/^\d{4}$/.test(raw)) {
      err.textContent = 'Please enter a 4 digit Australian postcode.';
      err.hidden = false;
      return;
    }

    var match = zoneFor(raw);
    if (!match && POST_ON) {
      err.hidden = true;
      state.postcode = raw;
      state.zoneKey = '';
      state.dateISO = '';
      state.bakeryISO = '';
      state.posted = true;
      saveLocal();
      renderSummary();
      showStep('posted');
      return;
    }
    if (!match) {
      err.hidden = true;
      // Tag the waitlist signup with the postcode so expansion demand is countable.
      var tagField = q('[data-vpd-waitlist-tags]');
      if (tagField) tagField.value = 'waitlist, waitlist-' + raw;
      var echo = q('[data-vpd-waitlist-echo]');
      if (echo) echo.textContent = raw;
      showStep('waitlist');
      return;
    }

    err.hidden = true;
    state.posted = false;
    var zoneChanged = match.key !== state.zoneKey;
    state.postcode = raw;
    state.zoneKey = match.key;
    if (zoneChanged) { state.dateISO = ''; state.bakeryISO = ''; }
    saveLocal();
    showStep(isMixed() && !state.split ? 'split' : 'date');
  }

  /**
   * The entry gate opens the modal ~600ms after init, but the live Sheet takes
   * longer than that to arrive, so a postcode typed in the gap is judged
   * against the published snapshot. A postcode the snapshot has never heard of
   * lands the customer on the waitlist - "we do not deliver to you" - and
   * nothing used to re-check once the real schedule showed up, so a deliverable
   * customer stayed parked on a signup form. Re-run the postcode the moment the
   * Sheet lands. Returns true if the customer was moved off the waitlist.
   */
  function recheckWaitlist() {
    if (!root || root.hidden) return false;
    var step = q('[data-vpd-step="waitlist"]');
    if (!step || step.hidden) return false;

    var input = q('[data-vpd-postcode-input]');
    var raw = ((input && input.value) || '').trim();
    if (!/^\d{4}$/.test(raw) || !zoneFor(raw)) return false;

    submitPostcode();   // same happy path as if they had typed it just now
    return true;
  }

  // Save, write to the cart, close, and release any add-to-cart we intercepted.
  function commit() {
    saveLocal();
    renderSummary();
    return pushToCart().then(function () {
      var form = pendingForm;
      closeModal();
      if (form) {
        if (form.requestSubmit) form.requestSubmit();
        else form.submit();
      }
    });
  }

  function chooseSplit(choice) {
    state.split = choice;
    if (choice === 'Separate') {
      var b = nextBakeryDate();
      state.bakeryISO = b ? b.iso : '';
    } else {
      state.bakeryISO = '';
    }
    // A date picked before the split question may not survive the new rules.
    if (cartKnown && state.dateISO && !isStillValid()) state.dateISO = '';

    // The option the customer just picked already quoted a date. Asking them to
    // choose it a second time would contradict that, so only fall through to
    // the date step when something is genuinely still unanswered - typically
    // "deliver together", which invalidates a grocery-only date.
    if (isComplete()) {
      commit();
      return;
    }
    saveLocal();
    renderSummary();
    showStep('date');
  }

  function pickDate(iso) {
    state.dateISO = iso;
    if (state.split === 'Separate' && !state.bakeryISO) {
      var b = nextBakeryDate();
      state.bakeryISO = b ? b.iso : '';
    }
    commit();
  }

  /* ------------------------------------------------------------- gating */

  /**
   * Run fn once a schedule exists, waiting at most a few seconds. minMs keeps
   * the old breathing room before the modal appears on a warm load.
   *
   * If no schedule ever arrives - a first-time visitor with the Sheet
   * unreachable - fn is never called and the store FAILS OPEN: no modal, no
   * blocked add-to-cart, no blocked checkout. The customer shops normally and
   * the delivery date is collected afterwards. Refusing the sale outright
   * would be the worse outcome.
   */
  function whenScheduleReady(fn, minMs) {
    var waited = 0;
    var step = 100;
    var limit = 8000;
    var tick = function () {
      if (haveSchedule()) { fn(); return; }
      if (waited >= limit) return;   // fail open
      waited += step;
      setTimeout(tick, step);
    };
    setTimeout(tick, minMs || 0);
  }

  function isAddToCartForm(form) {
    if (!form || form.tagName !== 'FORM') return false;
    var action = form.getAttribute('action') || '';
    return action.indexOf('/cart/add') > -1;
  }

  function onSubmitCapture(e) {
    var form = e.target;

    if (isAddToCartForm(form)) {
      if (!haveSchedule()) return;   // fail open: never block a sale on missing data
      // A gift card is emailed, not delivered. "Where are we delivering?" is a
      // nonsense question here, and an out-of-area answer would strand the
      // buyer on the waitlist screen instead of selling them the card. The
      // marker is put on the form by vp-gift.js, which builds the recipient
      // fields on that page.
      if (form.querySelector('[data-vpg-rcpt]')) return;
      if (isPosted()) {
        if (cfg.product && cfg.product.melbOnly && mainProductForms().some(function (pf) { return pf.contains(form); })) {
          e.preventDefault();
          e.stopImmediatePropagation();
          openModal('posted');
          pendingForm = form;   // lets renderPosted name the product; Continue drops it
          renderPosted();
        }
        return;
      }
      if (state.dateISO && state.zoneKey && (!cartKnown || isStillValid())) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      pendingForm = form;
      if (cartKnown && state.dateISO && !isStillValid()) state.dateISO = ''; // cutoff passed while browsing
      openModal(state.zoneKey ? firstUnansweredStep() : 'postcode');
      return;
    }

    // Cart page and drawer both post a form with id="cart".
    if (form.id === 'cart' && !meetsMinimum()) {
      // No modal for this one. There is nothing to answer - they just need
      // more in the basket - and the warning above the button says how much.
      e.preventDefault();
      e.stopImmediatePropagation();
      syncCheckoutGate();
      return;
    }

    if (form.id === 'cart' && haveSchedule() && !isComplete()) {
      e.preventDefault();
      e.stopImmediatePropagation();
      openModal(firstUnansweredStep());
    }
  }

  function onClickCapture(e) {
    var btn = e.target.closest ? e.target.closest('[name="checkout"]') : null;
    if (!btn || btn.hasAttribute('data-vpd-ignore')) return;
    if (!meetsMinimum()) {
      e.preventDefault();
      e.stopImmediatePropagation();
      syncCheckoutGate();
      return;
    }
    if (!haveSchedule()) return;   // fail open
    if (isComplete()) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    openModal(firstUnansweredStep());
  }

  /* --------------------------------------------------------------- boot */

  function buildPostedStep() {
    if (!POST_ON || q('[data-vpd-step="posted"]')) return;
    var panel = root.querySelector('.vpd-modal__panel');
    if (!panel) return;
    var el = document.createElement('div');
    el.setAttribute('data-vpd-step', 'posted');
    el.hidden = true;
    el.innerHTML =
      '<h2>We\'ll post your order</h2>' +
      '<p>We don\'t deliver to <strong data-vpd-posted-echo></strong> yet, so your order will be posted to you.</p>' +
      '<p>Fridge, frozen and fresh-baked items are <strong>Melbourne delivery only</strong> and can\'t be posted.</p>' +
      '<p data-vpd-posted-terms hidden></p>' +
      '<p class="vpd-error" data-vpd-posted-warn hidden></p>' +
      '<button type="button" class="vpd-btn" data-vpd-posted-continue>Continue shopping</button>' +
      '<p class="vpd-posted-links">' +
        '<button type="button" class="vpd-link" data-vpd-change-postcode>Change postcode</button> · ' +
        '<button type="button" class="vpd-link" data-vpd-show-waitlist>Tell me when local delivery reaches me</button>' +
      '</p>';
    panel.appendChild(el);
  }

  function renderPosted() {
    var echo = q('[data-vpd-posted-echo]');
    if (echo) echo.textContent = state.postcode;
    // Drawn per postcode, because regional VIC and interstate pay different postage.
    var terms = q('[data-vpd-posted-terms]');
    if (terms) {
      var rate = postRate();
      terms.hidden = !(rate || POST_MIN);
      terms.innerHTML =
        (rate ? 'Postage is a flat <strong>' + escapeHtml(rate) + '</strong>' : 'Posted orders') +
        (POST_MIN ? (rate ? ', with a ' : ' have a ') + '<strong>' + money(POST_MIN) + ' minimum order</strong>.' : '.');
    }
    var warn = q('[data-vpd-posted-warn]');
    if (!warn) return;
    // Two different situations, two different messages: a product blocked on
    // its own page never reached the cart, so "remove it" would be wrong.
    var parts = [];
    var blocked = cfg.product && cfg.product.melbOnly && pendingForm ? cfg.product.title : '';
    if (blocked) {
      parts.push(blocked + ' is Melbourne delivery only, so it can\'t be added to an order posted to ' + state.postcode + '.');
    }
    var inCart = cart.melbOnly.filter(function (n) { return n !== blocked; });
    if (inCart.length) {
      parts.push('Already in your cart and can\'t be posted: ' + inCart.join(', ') + '. Please remove ' +
        (inCart.length > 1 ? 'them' : 'it') + ' to check out.');
    }
    var short = shortfall();
    if (short > 0 && cart.total > 0) {
      parts.push('Your cart is ' + money(short) + ' short of the ' + money(POST_MIN) + ' minimum.');
    }
    warn.hidden = !parts.length;
    warn.textContent = parts.join(' ');
  }

  function bindModal() {
    buildPostedStep();
    root.addEventListener('click', function (e) {
      var t = e.target;

      if (t.closest('[data-vpd-posted-continue]')) {
        // A melbourne-only add that we intercepted is dropped, not released.
        if (cfg.product && cfg.product.melbOnly) pendingForm = null;
        commit();
        return;
      }
      if (t.closest('[data-vpd-show-waitlist]')) {
        var tagField = q('[data-vpd-waitlist-tags]');
        if (tagField) tagField.value = 'waitlist, waitlist-' + state.postcode;
        var wEcho = q('[data-vpd-waitlist-echo]');
        if (wEcho) wEcho.textContent = state.postcode;
        showStep('waitlist');
        return;
      }

      if (t.closest('[data-vpd-close]') || t.hasAttribute('data-vpd-overlay')) {
        closeModal();
        try { localStorage.setItem(LS_DISMISS, '1'); } catch (err) { /* noop */ }
        return;
      }
      if (t.closest('[data-vpd-postcode-submit]')) { submitPostcode(); return; }
      if (t.closest('[data-vpd-change-postcode]')) { showStep('postcode'); return; }
      if (t.closest('[data-vpd-change-split]')) { showStep('split'); return; }

      var split = t.closest('[data-vpd-split]');
      if (split) { chooseSplit(split.getAttribute('data-vpd-split')); return; }

      var pick = t.closest('[data-vpd-pick]');
      if (pick) pickDate(pick.getAttribute('data-vpd-pick'));
    });

    root.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeModal();
      if (e.key === 'Enter' && e.target.matches('[data-vpd-postcode-input]')) {
        e.preventDefault();
        submitPostcode();
      }
    });
  }

  /**
   * Collection cards carry their own quick-add <product-form>, nested inside a
   * <li> of .product-grid-item__actions. Anything injected next to those forms
   * lands inside the button column and breaks it, so only the main product
   * form on a product template ever qualifies.
   */
  function mainProductForms() {
    if (!cfg.product) return [];
    return Array.prototype.filter.call(
      document.querySelectorAll('product-form'),
      function (pf) {
        return !pf.closest(
          '.product-grid-item, .product-grid-item__actions, quick-view-modal, ' +
          '#quickViewWrapper, .quick-view, cart-notification, #main-cart-footer'
        );
      }
    );
  }

  function injectProductEta() {
    mainProductForms().forEach(function (pf) {
      if (pf.querySelector('[data-vpd-eta]') || pf.nextElementSibling &&
          pf.nextElementSibling.hasAttribute && pf.nextElementSibling.hasAttribute('data-vpd-eta')) return;
      var el = document.createElement('div');
      el.className = 'vpd-eta';
      el.setAttribute('data-vpd-eta', '');
      el.hidden = true;
      pf.parentNode.insertBefore(el, pf.nextSibling);
    });
  }

  /**
   * Product page: a sage "Made to Order" pill explaining the cutoff.
   * cfg.product.madeToOrder is resolved in Liquid from the product's tags, so
   * detection here is exact rather than inferred from the DOM.
   *
   * Collection card badges are rendered by snippets/product-badge.liquid, not
   * here: server-side means no flash and no handle list to keep in sync.
   */
  function injectProductPill() {
    if (!MTO || !cfg.product || !cfg.product.madeToOrder) return;

    mainProductForms().forEach(function (pf) {
      if (pf.parentNode.querySelector('[data-vpd-mto-pill]')) return;
      var el = document.createElement('div');
      el.className = 'vpd-mto-pill';
      el.setAttribute('data-vpd-mto-pill', '');
      el.innerHTML =
        '<span class="vpd-mto-pill__tag">' + escapeHtml(MTO.label || 'Made to Order') + '</span>' +
        '<span class="vpd-mto-pill__text">' + escapeHtml(MTO.note || '') + '</span>';
      pf.parentNode.insertBefore(el, pf);
    });
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function buildCartEditor(compact) {
    var box = document.createElement('div');
    box.setAttribute('data-vpd-cart', '');
    box.className = 'vpd-cart' + (compact ? ' vpd-cart--compact' : '');
    box.innerHTML =
      '<div>' +
        '<p class="vpd-cart__label">Delivery date</p>' +
        '<p class="vpd-cart__value" data-vpd-summary></p>' +
        '<p class="vpd-cart__zone" data-vpd-zone-summary></p>' +
        '<p class="vpd-cart__bakery" data-vpd-bakery-line hidden></p>' +
      '</div>' +
      '<button type="button" class="vpd-btn" data-vpd-open>Change</button>';

    var warn = document.createElement('div');
    warn.setAttribute('data-vpd-cart-warning', '');
    warn.className = 'vpd-cart-warning';
    warn.textContent = 'Please choose a delivery date before checking out.';
    warn.hidden = true;

    return { box: box, warn: warn };
  }

  /**
   * Cart page and cart drawer both get a summary + Change button and a warning
   * above their checkout button. This store uses the drawer, so leaving it out
   * would hide the whole decision from most customers.
   *
   * Injected rather than templated so main-cart-footer.liquid and
   * cart-notification.liquid stay untouched, and placed outside .js-contents so
   * the theme's partial re-renders cannot wipe it. Safe to call repeatedly.
   */
  function injectCartEditors() {
    // Cart page
    var footer = document.querySelector('#main-cart-footer .cart__footer');
    if (footer && !footer.querySelector('[data-vpd-cart]')) {
      var a = buildCartEditor(false);
      footer.insertBefore(a.box, footer.firstChild);
      footer.insertBefore(a.warn, a.box.nextSibling);
    }

    // Cart drawer
    var drawer = document.querySelector('#cart-notification .cart_notification_links_inner');
    if (drawer && !drawer.querySelector('[data-vpd-cart]')) {
      var b = buildCartEditor(true);
      drawer.insertBefore(b.box, drawer.firstChild);
      drawer.insertBefore(b.warn, b.box.nextSibling);
    }
  }

  function reconcile() {
    // A stored postcode whose zone was removed from the Sheet must not stick -
    // but only judge that against the live Sheet, never the snapshot, or a
    // postcode added to the Sheet since the last snapshot gets thrown away.
    if (state.postcode && !zoneFor(state.postcode)) {
      if (cartKnown) {
        state.zoneKey = '';
        state.dateISO = '';
      }
    } else if (state.postcode) {
      // ALWAYS re-derive from the postcode; never just fill a blank, and never
      // only patch a key that is missing from the current data.
      //
      // A zone's key is built from its delivery-day set (days.join('-')), so
      // the snapshot and the live Sheet disagree about keys constantly - the
      // snapshot maps 3079 to 'wed-sat' while the Sheet maps it to
      // 'mon-wed-sat-sun'. A stale key that happens to EXIST in the other data
      // set is the dangerous case: it silently puts the customer in the wrong
      // delivery zone instead of merely re-asking. zoneFor() is the only
      // authority, and re-deriving is idempotent, so a later pass always
      // corrects whatever an earlier, less informed one guessed.
      state.zoneKey = zoneFor(state.postcode).key;
    }
    // Same rule for the date: availableDates() depends on the Sheet AND on
    // cart.hasBakery, so before the cart lands it can rule out a good date.
    if (cartKnown && state.dateISO && !isStillValid()) state.dateISO = '';
    if (cartKnown && state.split === 'Separate' && state.bakeryISO) {
      var ok = availableDates(state.zoneKey, true)
        .some(function (d) { return d.iso === state.bakeryISO; });
      if (!ok) {
        var b = nextBakeryDate();
        state.bakeryISO = b ? b.iso : '';
      }
    }
  }

  function init() {
    root = document.getElementById('vpd-modal');
    if (!root) return;

    // Source ranking, best first:
    //   1. the Sheet          - arrives ~800ms in, always wins when it lands
    //   2. this cache         - a real copy of the Sheet from this customer's
    //                           last visit, so at worst days old
    //   3. the metafield      - a hand-seeded snapshot with no sync job behind
    //                           it, and demonstrably years-stale-able
    //
    // The cache outranks the snapshot because it cannot rot without someone
    // visiting, while the snapshot rots on its own. loadSchedule() already
    // refuses anything older than 30 days or too small to be real.
    //
    // (This has to run here, not at the top of the file: LS_SCHEDULE is a var
    // declared further down, so up there it is hoisted but still undefined.)
    var cached = loadSchedule();
    if (cached) {
      DATA.zones = cached.zones;
      DATA.postcodes = cached.postcodes;
    }

    loadLocal();
    reconcile();

    bindModal();
    watchCartMutations();
    injectProductEta();
    injectProductPill();
    injectCartEditors();
    renderSummary();

    document.addEventListener('submit', onSubmitCapture, true);
    document.addEventListener('click', onClickCapture, true);

    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-vpd-open]')) {
        e.preventDefault();
        openModal();
      }
    });

    // The snapshot has already rendered by this point. Refresh the cart and the
    // live schedule in parallel, then reconcile once against both.
    Promise.all([refreshCart(), loadSheet()]).then(function (results) {
      cartKnown = true;
      loadLocal(); // restore anything an earlier pass could not validate yet
      reconcile();
      // Persist the reconciled result. Without this the corrections above are
      // in-memory only: localStorage keeps the stale zoneKey and every future
      // page load repeats the same guess off whatever booted first.
      saveLocal();
      injectCartEditors();
      renderSummary();
      // A schedule change can invalidate whatever the modal is currently
      // showing, so redraw it if the customer already has it open.
      if (results[1] && root && !root.hidden) {
        if (!recheckWaitlist()) showStep(currentStep());
      }
    });

    // Belt and braces alongside watchCartMutations(): the cart page's own
    // quantity handler does publish this event.
    if (window.subscribe && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, function () {
        setTimeout(function () {
          refreshCart().then(function () {
            reconcile();
            injectCartEditors();
            renderSummary();
          });
        }, 0);
      });
    }

    // Entry gate on first visit.
    var dismissed = false;
    try { dismissed = localStorage.getItem(LS_DISMISS) === '1'; } catch (e) { /* noop */ }
    if (!state.postcode && !dismissed && !(window.Shopify && window.Shopify.designMode)) {
      // Fires on a schedule being available rather than on a fixed timer: the
      // old 600ms timer beat the Sheet home and asked the postcode question
      // against the stale snapshot.
      whenScheduleReady(function () { openModal('postcode'); }, 600);
    }

    if (state.dateISO) saveLocal();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
